import React, { useEffect, useRef, useState } from 'react';
import axios, { AxiosError } from 'axios';
import {
  LogInWrapper,
  LogInFormContainer,
  LogInForm,
  LogInTitle,
  LogInSubTitle,
  ButtonContainer,
  ButtonTitle,
  ButtonContent,
  ButtonSpinner,
  ButtonSuccess,
  InputContainer,
  Label,
  TextContainer,
  StyledInput,
  CompanyTitle,
  Description,
  BrandRow,
  HeroEyebrow,
  HeroContent,
  HeroMeta,
  PasswordToggle,
  PasswordEye,
  ErrorMessage,
  LegalCorner,
} from './login.styled';
import { LogoMark } from '../logo/logo-mark';
import { RightArrow } from '../icons/right-arrow';
import { Eye } from '../icons/eye';
import toast from '../../utils/toast';
import { apiRoute } from '../../constants/api-constants';
import { Spinner } from '../icons/spinner';
import { useAuth } from '../../context/auth-provider';
import { DefaultComponentProps } from '../app-router/component-props';
import { Link, useLocation } from 'react-router-dom';
import { resolvePostAuthRoute } from '../../utils/post-auth-route';
import { TopLink } from '../onboarding/onboarding.styled';
import { InlineMessage, Pop, Shake } from '../../motion/feedback';
import { DURATION } from '../../motion/tokens';
import { CheckRing } from '../icons/check-ring';
import { LegalLinks } from '../legal/legal-links';
import { getStaysSignupConfig } from '../../api/stays-signup-client';
import { useStaysProduct } from '../../utils/app-features';

/* HOW LONG THE TICK HOLDS BEFORE THE APP ARRIVES.
   Long enough to be read as an answer, short enough that nobody waits for it.
   The route layer then rises the app up out of this card ('deep' direction in
   route-transition.tsx), so signing in is one continuous gesture: press,
   tick, arrive. */
const SUCCESS_HOLD_MS = DURATION.emphasis;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

interface LogInData {
  email: string;
  password: string;
}

export const LogIn: React.FC<DefaultComponentProps> = ({ navigate }) => {
  /* ARRIVED HERE FROM AN ADDRESS THAT MATCHED NOTHING.
     PublicRoutes sends every unmatched path to this form (public-routes.tsx),
     which is the right destination and was a completely silent one — the
     visitor met a sign-in screen with no account of why. Deliberately worded
     for BOTH cases that land here: a mistyped or expired public link, and a
     private address that simply needs a session. */
  const location = useLocation();
  const arrivedUnmatched = Boolean(
    (location.state as { unmatched?: boolean } | null)?.unmatched
  );
  const [logInData, setLogInData] = useState<LogInData>({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [succeeded, setSucceeded] = useState<boolean>(false);
  const [invalidCredentials, setInvalidCredentials] = useState<boolean>(false);
  // Counts refusals so the second wrong password shakes the fields again —
  // a prop-driven keyframe only replays on remount.
  const [attempt, setAttempt] = useState<number>(0);
  /* TWO-STEP SIGN-IN. Set when the password was right but the account wants a
     code: the card becomes the code step, and no session exists until the
     code is accepted (POST /auth/mfa/verify). */
  const [mfaChallenge, setMfaChallenge] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState<string>('');
  const [mfaError, setMfaError] = useState<string | null>(null);
  const submitLockRef = useRef(false);
  const succeededRef = useRef(false);

  const { signIn, user, onboarding, permissions, setAccountClosing } = useAuth();

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    // THE PASSWORD IS STORED EXACTLY AS TYPED. DO NOT TRIM IT.
    //
    // This used to run value.trim() on the password on every keystroke. Because
    // it ran per character, the trailing space was deleted before the next
    // character arrived, so a passphrase with internal spaces — "open the door"
    // — could not be typed at all; it arrived as "openthedoor". And none of the
    // three password-SETTING forms (signup, invite-accept, reset-password) trim,
    // so a password created with a space could be set and then never entered
    // again. The user was told "Invalid email or password", which is the one
    // message that guarantees they keep retrying the password that is correct.
    // Reset did not rescue them either: it let them set the same value back.
    //
    // bcrypt accepts spaces and the backend imposes no charset rule, so the only
    // correct behaviour is to send exactly what was typed.
    setLogInData({ ...logInData, [name]: value });
    if (invalidCredentials) {
      setInvalidCredentials(false);
    }
  };

  /* THE ANSWER FIRST, THEN THE APP. The button turns into a tick and holds for
     one beat while the form stays exactly where it is; only then does the
     session flip and the route change. The old order — spinner stops, toast,
     and the screen is already gone — read as the page reloading. Shared by the
     password step and the code step, so finishing in two steps looks exactly
     like finishing in one. */
  const finishSignIn = async (data: {
    user: Parameters<typeof signIn>[0];
    onboarding?: Parameters<typeof signIn>[1];
    permissions?: string[];
    account_closing?: boolean;
  }) => {
    succeededRef.current = true;
    setSucceeded(true);
    await wait(SUCCESS_HOLD_MS);
    toast.success('You have successfully logged in');
    // One call, so authStatus moves to 'authed' with the rest of the session
    // state. Setting user without authStatus leaves RootRouter bouncing
    // between '/' and the dashboard forever.
    // Independent Stays IS8: the owner of a frozen account lands on the
    // closing screen, not on a dashboard whose every call is refused.
    setAccountClosing(data.account_closing === true);
    signIn(data.user, data.onboarding || null, data.permissions || []);

    navigate(
      resolvePostAuthRoute(data.user, data.onboarding ?? null, data.permissions || [])
    );
  };

  const onSubmitCode: React.FormEventHandler<HTMLFormElement> = async e => {
    e.preventDefault();

    if (submitLockRef.current || !mfaChallenge) {
      return;
    }

    submitLockRef.current = true;
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${apiRoute}/auth/mfa/verify`,
        { challenge: mfaChallenge, code: mfaCode.trim() },
        { headers: { 'Content-Type': 'application/json' } }
      );

      await finishSignIn(response.data);
    } catch (error) {
      const status = (error as AxiosError).response?.status;

      if (status === 401) {
        setMfaError('That code is not right. Try the newest code from your app.');
        setAttempt((n) => n + 1);
      } else if (status === 410) {
        // Five wrong codes, five minutes, or the account locked: back to the
        // password, with the server's reason (it says which).
        setMfaChallenge(null);
        setLogInData((d) => ({ ...d, password: '' }));
        toast.error(
          (error as AxiosError<{ message?: string }>).response?.data?.message ||
            'That sign-in expired. Enter your password again.'
        );
      } else if (status === 429) {
        toast.error('Too many attempts. Wait a few minutes and try again.');
      } else if (!(error as AxiosError).response) {
        toast.error('Unable to reach Lobbero. Check your connection and retry.');
      } else {
        toast.error('Lobbero is having trouble right now. Please try again shortly.');
      }
    } finally {
      if (!succeededRef.current) {
        submitLockRef.current = false;
        setIsLoading(false);
      }
    }
  };

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async e => {
    e.preventDefault();

    if (submitLockRef.current) {
      return;
    }

    submitLockRef.current = true;
    setIsLoading(true);
    try {
      const response = await axios.post(apiRoute as string, logInData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      setInvalidCredentials(false);

      // The password was right but the account wants a code: no session was
      // issued, so the card switches to the code step instead of finishing.
      if (response.data && response.data.mfa_required) {
        setMfaChallenge(response.data.challenge);
        setMfaCode('');
        setMfaError(null);

        return;
      }

      // No token to store: the server set httpOnly session cookies on this
      // response, and JS deliberately cannot read them.
      await finishSignIn(response.data);
    } catch (error) {
      const axiosError = error as AxiosError;

      if (axiosError.response) {
        /* THREE STATUSES THAT MEAN THREE DIFFERENT THINGS.
           This handled 401 and sent everything else to "An unexpected error
           occurred" — including 429, which is the one case where the user is
           doing nothing wrong and the correct advice is "wait", and 403, which
           means the account exists and is closed. Telling a locked-out person
           to "try again later" when they are locked out BECAUSE they tried
           again is the least useful sentence available. */
        switch (axiosError.response.status) {
          case 401:
            setInvalidCredentials(true);
            setAttempt((n) => n + 1);
            toast.error('Invalid email or password');
            break;
          case 403:
            // Two refusals share the status and say different things: the
            // whole organisation is suspended (only Lobbero can lift it), or
            // this one account was switched off (an administrator can).
            {
              const refusedCode = (
                axiosError.response.data as { code?: string } | undefined
              )?.code;
              toast.error(
                refusedCode === 'ORG_SUSPENDED'
                  ? 'This organization is suspended, so nobody in it can sign in. If you think this is a mistake, email hello@lobbero.com.'
                  : refusedCode === 'ACCOUNT_CLOSING'
                    ? "This account's owner has closed it, so nobody can sign in. Only the owner can reopen it."
                    : 'This account has been deactivated. Ask an administrator to restore it.'
              );
            }
            break;
          case 429:
            toast.error(
              'Too many sign-in attempts. Wait a few minutes and try again.'
            );
            break;
          case 500:
          case 502:
          case 503:
          case 504:
            toast.error(
              'Lobbero is having trouble right now. Please try again shortly.'
            );
            break;
          default:
            toast.error(
              'An unexpected error occurred. Please try again later.'
            );
        }
      } else {
        toast.error(
          'Unable to reach Lobbero. Check your connection and retry.'
        );
      }
    } finally {
      // A successful submit keeps the button in its finished state until the
      // route has moved on; releasing it here would flash "Sign In" back for a
      // frame between the tick and the app.
      if (!succeededRef.current) {
        submitLockRef.current = false;
        setIsLoading(false);
      }
    }
  };

  // ALREADY SIGNED IN (a session restored on reload, or a second tab that
  // signed in) — go home. NOT after this form's own submit: that path
  // navigates itself once the tick has held, and doing it here as well pushed
  // a second, identical history entry so Back landed on the same screen.
  // "New to Lobbero Stays?" is offered only while Stays sign-up is open
  // (Independent Stays IS3; closed on production until launch). A failed read
  // simply leaves the link out.
  // IS8.0: only when Lobbero Stays exists on this server (the master switch). Without
  // it the route is not there, and asking would be a failed request on every screen.
  const staysProduct = useStaysProduct();
  const [staysSignupOpen, setStaysSignupOpen] = useState(false);
  useEffect(() => {
    if (staysProduct !== true) return undefined;
    let live = true;
    getStaysSignupConfig()
      .then(c => {
        if (live) setStaysSignupOpen(c.mode === 'open' && !c.billing);
      })
      .catch(() => undefined);

    return () => {
      live = false;
    };
  }, [staysProduct]);

  useEffect(() => {
    if (user && !succeededRef.current) {
      navigate(resolvePostAuthRoute(user, onboarding, permissions));
    }
  }, [user]);

  return (
    <LogInWrapper id="log-in-form">
      {/* THE SKIP LINK IS MOUNTED ON THIS SCREEN TOO, and had nothing to
          resolve to: sign-in is built from its own wrapper rather than
          ContentWrapper, so `#main-content` did not exist and the first control
          in the tab order did nothing. Measured on `/`. */}
      <LogInFormContainer as="main" id="main-content" tabIndex={-1}>
        {mfaChallenge ? (
        <LogInForm onSubmit={onSubmitCode}>
          <LogInTitle>Two-step verification</LogInTitle>
          <LogInSubTitle>
            Enter the 6-digit code from your authenticator app — or one of your
            recovery codes.
          </LogInSubTitle>
          <InputContainer>
            <Label htmlFor="login-mfa-code">Code</Label>
            <Shake active={Boolean(mfaError)} attempt={attempt}>
            <StyledInput
              id="login-mfa-code"
              name="code"
              value={mfaCode}
              placeholder="123456"
              onChange={(event) => {
                setMfaCode(event.target.value);
                if (mfaError) setMfaError(null);
              }}
              hasError={Boolean(mfaError)}
              aria-invalid={Boolean(mfaError)}
              aria-describedby={mfaError ? 'login-mfa-error' : undefined}
              autoComplete="one-time-code"
              autoFocus
              required
            />
            </Shake>
          </InputContainer>
          <InlineMessage
            id="login-mfa-error"
            role="alert"
            message={
              mfaError ? <ErrorMessage as="span">{mfaError}</ErrorMessage> : undefined
            }
          />
          <ButtonContainer
            type="submit"
            isLoading={isLoading}
            disabled={isLoading}
            aria-busy={isLoading && !succeeded}
            data-login-state={succeeded ? 'done' : isLoading ? 'busy' : 'idle'}
          >
            <ButtonContent $hidden={isLoading}>
              <ButtonTitle>Verify</ButtonTitle>
              <RightArrow />
            </ButtonContent>
            {isLoading && !succeeded ? (
              <ButtonSpinner>
                <Spinner isId />
              </ButtonSpinner>
            ) : null}
            {succeeded ? (
              <ButtonSuccess>
                <Pop>
                  <CheckRing stroke="#FFFFFF" />
                </Pop>
                <ButtonTitle>Signed in</ButtonTitle>
              </ButtonSuccess>
            ) : null}
          </ButtonContainer>
          <TopLink>
            <Link
              to="/"
              onClick={() => {
                setMfaChallenge(null);
                setLogInData((d) => ({ ...d, password: '' }));
              }}
            >
              Use a different account
            </Link>
          </TopLink>
        </LogInForm>
        ) : (
        <LogInForm onSubmit={onSubmit}>
          <LogInTitle>Sign In</LogInTitle>
          <LogInSubTitle>
            {arrivedUnmatched
              ? 'That page needs you to be signed in, or it no longer exists. Sign in to continue.'
              : 'Enter the email address you use to sign in to Lobbero'}
          </LogInSubTitle>
          <InputContainer>
            <Label htmlFor="login-email">Email address</Label>
            <Shake active={invalidCredentials} attempt={attempt}>
            <StyledInput
              id="login-email"
              type="email"
              name="email"
              /* THE FIRST FIELD TAKES THE FOCUS.
                 This is the most-repeated form in the product — every member of
                 staff, every shift — and it opened with focus on the document,
                 so signing in began with a Tab press or a click every single
                 time. autoFocus is right here and almost nowhere else: this
                 page has one purpose, the form is the whole screen, and there
                 is nothing above it for the focus to steal. */
              autoFocus
              value={logInData.email}
              placeholder={'you@building.com'}
              onChange={handleInputChange}
              hasError={invalidCredentials}
              aria-invalid={invalidCredentials}
              aria-describedby={
                invalidCredentials ? 'login-error-message' : undefined
              }
              autoComplete="email"
              required
            />
            </Shake>
          </InputContainer>
          <InputContainer>
            <Label htmlFor="login-password">Password</Label>
            <Shake active={invalidCredentials} attempt={attempt}>
            <StyledInput
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={logInData.password}
              placeholder={'Enter your password'}
              onChange={handleInputChange}
              hasError={invalidCredentials}
              aria-invalid={invalidCredentials}
              aria-describedby={
                invalidCredentials ? 'login-error-message' : undefined
              }
              autoComplete="current-password"
              required
            />
            </Shake>
            <PasswordToggle
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {/* Two crossfading SVGs rather than one Font Awesome glyph
                  swapped by className: the stylesheet they needed was
                  render-blocking, third-party, and on the critical path of this
                  screen. The button's aria-label already carries the meaning,
                  so both are hidden from assistive tech. */}
              <PasswordEye visible={showPassword} aria-hidden="true">
                <Eye />
              </PasswordEye>
              <PasswordEye visible={!showPassword} aria-hidden="true">
                <Eye slashed />
              </PasswordEye>
            </PasswordToggle>
          </InputContainer>
          {/* The line UNFOLDS under the password field rather than being
              inserted: the button below slides down to make room instead of
              jumping, and the space closes again the moment typing resumes. */}
          <InlineMessage
            id="login-error-message"
            role="alert"
            message={
              invalidCredentials ? (
                <ErrorMessage as="span">
                  Invalid email or password. Please try again.
                </ErrorMessage>
              ) : undefined
            }
          />
          <ButtonContainer
            type="submit"
            isLoading={isLoading}
            disabled={isLoading}
            aria-busy={isLoading && !succeeded}
            data-login-state={
              succeeded ? 'done' : isLoading ? 'busy' : 'idle'
            }
          >
            {/* Idle label, pending ring and the finished tick all live in the
                same 44px box, so the button never changes size while it moves
                idle → busy → done. */}
            <ButtonContent $hidden={isLoading}>
              <ButtonTitle>Sign In</ButtonTitle>
              <RightArrow />
            </ButtonContent>
            {isLoading && !succeeded ? (
              <ButtonSpinner>
                <Spinner isId />
              </ButtonSpinner>
            ) : null}
            {succeeded ? (
              <ButtonSuccess>
                <Pop>
                  <CheckRing stroke="#FFFFFF" />
                </Pop>
                <ButtonTitle>Signed in</ButtonTitle>
              </ButtonSuccess>
            ) : null}
          </ButtonContainer>
          {/* Recovery for EVERY role. Until this existed a forgotten password
              was a permanent lockout: a password could only ever be set at
              signup or at invite-acceptance, and the founding owner of an
              organisation has nobody above them to reset it by hand. */}
          <TopLink>
            <Link to="/forgot-password">Forgot your password?</Link>
          </TopLink>
          <TopLink>
            New to Lobbero? <Link to="/signup">Create an account</Link>
          </TopLink>
          {staysSignupOpen ? (
            <TopLink>
              New to Lobbero Stays? <Link to="/signup/stays">Sign up</Link>
            </TopLink>
          ) : null}
        </LogInForm>
        )}
        <TextContainer>
          <BrandRow>
            <LogoMark size={48} tone="onBrand" aria-hidden="true" />
            <CompanyTitle>Lobbero</CompanyTitle>
            {/* The policies and the FAQ, readable before anyone signs in.
                In the brand panel beside the logo, so they are there on every
                sign-in step and stay out of the form's way. */}
            <LegalCorner>
              <LegalLinks tone="onBrand" />
            </LegalCorner>
          </BrandRow>
          <HeroContent>
            <HeroEyebrow>Concierge, remembered</HeroEyebrow>
            <Description>
              The best doorman you&rsquo;ve ever had &mdash; now with a memory
              that never forgets.
            </Description>
            <HeroMeta>
              Guests welcomed, deliveries logged, every arrival handled &mdash;
              so your front desk always knows who&rsquo;s coming.
            </HeroMeta>
          </HeroContent>
        </TextContainer>
      </LogInFormContainer>
    </LogInWrapper>
  );
};
